/**
 * Telling the draft apart from what is published.
 *
 * The service has no flag for "the draft has unpublished changes" — the draft
 * and the versions are separate documents and neither mentions the other — so
 * the comparison is made here, between the draft and the latest version's
 * frozen copy. This goes away if that is reported on the draft.
 */

import { AOPPlan, AOPVersion } from '../../../types/AOPTypes';

/**
 * JSON with object keys in a fixed order, so two documents that differ only in
 * the order their fields came back in still compare equal. The draft store and
 * the version store serialise independently, so field order is not something we
 * can lean on.
 */
const canonicalise = (value: any): string => {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value === undefined ? null : value);
	}

	if (Array.isArray(value)) {
		return `[${value.map(canonicalise).join(',')}]`;
	}

	const keys = Object.keys(value).sort();
	const pairs = keys
		.filter((key) => value[key] !== undefined)
		.map((key) => `${JSON.stringify(key)}:${canonicalise(value[key])}`);

	return `{${pairs.join(',')}}`;
};

/** Whether a draft would publish to something identical to `version`. */
export const isSameAsVersion = (
	draftProse: string | null,
	draftPlan: AOPPlan | null,
	version: AOPVersion
): boolean => {
	if (!draftPlan) {
		return false;
	}

	// The draft's prose is nullable and a version's is not, so an empty draft
	// prose and an empty published prose are the same document.
	const prose = draftProse === null || draftProse === undefined ? '' : draftProse;

	return prose === version.prose && canonicalise(draftPlan) === canonicalise(version.plan);
};

/** Which document the canvas is showing, and therefore how it behaves. */
export type AOPVersionMode = 'draft' | 'published' | 'superseded';

export const versionModeOf = (
	selectedVersion: number | null,
	publishedVersion: number | null
): AOPVersionMode => {
	if (selectedVersion === null || selectedVersion === undefined) {
		return 'draft';
	}

	return selectedVersion === publishedVersion ? 'published' : 'superseded';
};

/**
 * How each mode is dressed: the accent line along the top of the canvas and the
 * status pill that floats over it. Tailwind only sees class names it can find
 * as literals, so these are whole strings rather than something interpolated.
 */
export interface AOPVersionChrome {
	/** Accent line along the top edge of the canvas. */
	border: string;
	/** The floating status pill's surface. */
	pill: string;
	/** The version name inside the pill. */
	accentText: string;
	/** The descriptor after it. */
	mutedText: string;
	/** Whether the pill's buttons sit on a dark surface. */
	onDark: boolean;
	/**
	 * The pill's action buttons. On a dark pill antd's `ghost` already paints
	 * them white; on the light draft pill they are filled with the accent, which
	 * antd has no type for — and antd's stylesheet loads after Tailwind's, so
	 * every declaration has to win with `!`.
	 */
	button: string;
}

export const AOP_VERSION_CHROME: { [mode: string]: AOPVersionChrome } = {
	// Each mode's pill is the same hue as its accent line. The published and
	// superseded pills take the line's colour outright; the draft one takes the
	// pale tint of it instead — a draft is the mode you sit in for an hour, and a
	// full-strength gold bar across the top of the canvas the whole time is too
	// much. The thin line stays saturated, so the signal survives.
	draft: {
		border: 'border-t-calendulaGold-5',
		// The pill's two colours are off-system and live in AOPBuilder.less; the
		// text inherits from it rather than being set again here.
		pill: 'aop-draft-pill border border-solid border-calendulaGold-5',
		accentText: '',
		mutedText: 'aop-draft-pill-muted',
		onDark: false,
		button:
			'!border !border-solid !border-calendulaGold-5 !bg-transparent !text-character-primary hover:!bg-calendulaGold-5',
	},
	published: {
		border: 'border-t-purple-6',
		pill: 'bg-purple-6',
		accentText: 'text-grey-1',
		mutedText: 'text-purple-2',
		onDark: true,
		button: '',
	},
	superseded: {
		border: 'border-t-grey-10',
		pill: 'bg-grey-10',
		accentText: 'text-grey-1',
		mutedText: 'text-grey-5',
		onDark: true,
		button: '',
	},
};
